import {EventData} from './cml-types';

/**
 * Ordre des événements de « Configurer ma liste » : le même que la liste des événements du BO Online
 * (front-bo-online, events.controller.js : computeEvtStatus / sectionIndexOf / sortEventsForDisplay),
 * pour que l'organisateur retrouve ses événements au même endroit d'un back-office à l'autre (#2000).
 *
 * Sections, dans l'ordre : épinglés, en cours, à venir, en stock, clôturés, passés. Dans une section,
 * le début le plus proche d'abord ; clôturés et passés vont du plus récent au plus ancien.
 */
const PINNED = 0;
const ONGOING = 1;
const UPCOMING = 2;
const STOCK = 3;
const CLOSED = 4;
const PAST = 5;

/** « DD/MM/YYYY HH:mm » (heure facultative) en horodatage local, ou null si illisible. */
export function parseEventDate(value: string | undefined): number | null {
  const m = value?.match(/^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}))?/);
  if (!m) return null;
  return new Date(+m[3], +m[2] - 1, +m[1], +(m[4] ?? 0), +(m[5] ?? 0)).getTime();
}

function sectionOf(ev: EventData, now: number): number {
  const end = parseEventDate(ev.endDate);
  const start = parseEventDate(ev.startDate);
  // Même priorité que computeEvtStatus : « en stock » l'emporte, puis passé, puis clôturé.
  let section: number;
  if (ev.first === true) section = STOCK;
  else if (end !== null && now > end) section = PAST;
  else if (ev.status === 'clotured') section = CLOSED;
  else if (start !== null && now < start) section = UPCOMING;
  else section = ONGOING;
  // Un événement épinglé remonte en tête, sauf s'il est passé ou clôturé.
  return ev.favorite === true && section !== PAST && section !== CLOSED ? PINNED : section;
}

export function sortEventsForDisplay<T extends EventData>(events: T[], now = Date.now()): T[] {
  return events
    .map((ev) => ({ev, section: sectionOf(ev, now), start: parseEventDate(ev.startDate)}))
    .sort((a, b) => {
      if (a.section !== b.section) return a.section - b.section;
      // Sans date de début exploitable, l'événement passe en fin de section.
      if (a.start === null && b.start === null) return 0;
      if (a.start === null) return 1;
      if (b.start === null) return -1;
      const ascending = a.section !== CLOSED && a.section !== PAST;
      return ascending ? a.start - b.start : b.start - a.start;
    })
    .map(({ev}) => ev);
}
